import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Flex, HStack, VStack, SimpleGrid, Text, Heading, Badge, Button, Divider, Spinner,
    FormControl, FormLabel, FormHelperText, Input, Switch, Checkbox, Tooltip,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Alert, AlertIcon, AlertTitle, AlertDescription, useToast,
} from '@chakra-ui/react';
import { FaSave, FaUndo } from 'react-icons/fa';
import dayjs from 'dayjs';
import { useConfirm } from '../components/ConfirmDialog';
import { getConfigConsultaEstoque, salvarConfigConsultaEstoque } from '../services/api';
import { hasPagePermission } from '../services/permission';

const DIAS = [
    { valor: 1, curto: 'Seg' },
    { valor: 2, curto: 'Ter' },
    { valor: 3, curto: 'Qua' },
    { valor: 4, curto: 'Qui' },
    { valor: 5, curto: 'Sex' },
    { valor: 6, curto: 'Sáb' },
    { valor: 7, curto: 'Dom' },
];

const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

// Mesma ordem da tela, para o resumo e a comparação com o que veio do servidor.
const CAMPOS = [
    'envio_ativo', 'dias_semana', 'horario',
    'fornecedores_desativados', 'itens_por_consulta', 'dias_validade',
    'dias_bloqueio_minimo', 'dias_apos_nao_tem', 'dias_apos_sem_resposta',
];

const ROTULOS = {
    envio_ativo: 'Envio automático',
    dias_semana: 'Dias da semana',
    horario: 'Horário',
    fornecedores_desativados: 'Fornecedores que participam',
    itens_por_consulta: 'Peças por lista',
    dias_validade: 'Validade do link',
    dias_bloqueio_minimo: 'Bloqueio mínimo',
    dias_apos_nao_tem: 'Repergunta após "não tenho"',
    dias_apos_sem_resposta: 'Repergunta sem resposta',
};

const paraFormulario = (config) => ({
    envio_ativo: !!config.envio_ativo,
    dias_semana: String(config.dias_semana ?? '1,3,5'),
    horario: String(config.horario ?? '14:00'),
    // Lista de quem NAO participa: fornecedor novo entra ligado, como sempre.
    fornecedores_desativados: [...(config.fornecedores_desativados ?? [])].map(Number).sort((a, b) => a - b),
    itens_por_consulta: String(config.itens_por_consulta ?? 15),
    dias_validade: String(config.dias_validade ?? 3),
    dias_bloqueio_minimo: String(config.dias_bloqueio_minimo ?? 15),
    dias_apos_nao_tem: String(config.dias_apos_nao_tem ?? 30),
    dias_apos_sem_resposta: String(config.dias_apos_sem_resposta ?? 7),
});

const paraEnvio = (form) => ({
    envio_ativo: !!form.envio_ativo,
    dias_semana: form.dias_semana,
    horario: form.horario,
    fornecedores_desativados: form.fornecedores_desativados,
    itens_por_consulta: Number(form.itens_por_consulta),
    dias_validade: Number(form.dias_validade),
    dias_bloqueio_minimo: Number(form.dias_bloqueio_minimo),
    dias_apos_nao_tem: Number(form.dias_apos_nao_tem),
    dias_apos_sem_resposta: Number(form.dias_apos_sem_resposta),
});

const soDigitos = (texto) => texto.replace(/\D/g, '').slice(0, 3);

// O numero vem do cadastro com DDI colado: na tela ele e so conferencia, entao
// basta ficar legivel.
const telefone = (bruto) => {
    const d = String(bruto || '').replace(/\D/g, '');
    const semDdi = d.startsWith('55') && d.length > 11 ? d.slice(2) : d;
    if (semDdi.length < 10) return bruto;
    const ddd = semDdi.slice(0, 2);
    const resto = semDdi.slice(2);
    const meio = resto.length === 9 ? resto.slice(0, 5) : resto.slice(0, 4);
    return `(${ddd}) ${meio}-${resto.slice(meio.length)}`;
};

/** Campo numérico com a faixa aceita vinda do backend, não chutada aqui. */
function CampoNumero({ rotulo, ajuda, valor, onChange, limites, unidade, somenteLeitura, erro }) {
    const { min, max } = limites || {};

    return (
        <FormControl isInvalid={!!erro}>
            <FormLabel fontSize="sm" fontWeight="semibold" mb={1}>{rotulo}</FormLabel>
            <HStack spacing={2} align="center">
                <Input
                    size="sm"
                    bg="white"
                    maxW="90px"
                    textAlign="center"
                    inputMode="numeric"
                    value={valor}
                    isDisabled={somenteLeitura}
                    onChange={(e) => onChange(soDigitos(e.target.value))}
                />
                {unidade && <Text fontSize="sm" color="gray.600">{unidade}</Text>}
            </HStack>
            <FormHelperText fontSize="xs" color={erro ? 'red.500' : 'gray.600'} mt={1}>
                {erro || ajuda}
                {!erro && min !== undefined && (
                    <Text as="span" color="gray.500"> (de {min} a {max})</Text>
                )}
            </FormHelperText>
        </FormControl>
    );
}

function Secao({ titulo, descricao, children }) {
    return (
        <Box borderWidth="1px" borderRadius="lg" bg="gray.50" p={{ base: 3, md: 5 }}>
            <Heading as="h4" size="sm" textAlign="left" mb={1}>{titulo}</Heading>
            <Text fontSize="xs" color="gray.600" mb={4}>{descricao}</Text>
            {children}
        </Box>
    );
}

export default function ConfigConsultaTab() {
    const toast = useToast();
    const { confirm, ConfirmPortal } = useConfirm();

    // Quem abre a tela vê os números; salvar é só de administrador, igual à rota.
    const podeSalvar = hasPagePermission('/configuracoes');

    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [form, setForm] = useState(null);
    const [inicial, setInicial] = useState(null);
    const [padrao, setPadrao] = useState({});
    const [limites, setLimites] = useState({});
    const [ensaio, setEnsaio] = useState(false);
    const [fornecedores, setFornecedores] = useState([]);
    const [autoria, setAutoria] = useState({ em: null, por: null });

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const data = await getConfigConsultaEstoque();
            if (!data?.success) {
                toast({ title: 'Erro ao carregar a configuração.', status: 'error', isClosable: true });
                return;
            }
            const vindo = paraFormulario(data.config || {});
            setForm(vindo);
            setInicial(vindo);
            setFornecedores(data.fornecedores || []);
            setPadrao(data.padrao || {});
            setLimites(data.limites || {});
            setEnsaio(!!data.ensaio);
            setAutoria({ em: data.atualizado_em, por: data.atualizado_por });
        } finally {
            setCarregando(false);
        }
    }, [toast]);

    useEffect(() => { carregar(); }, [carregar]);

    const alterar = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

    const diasMarcados = useMemo(() => {
        if (!form) return [];
        return String(form.dias_semana).split(',').filter(Boolean).map(Number);
    }, [form]);

    const participa = (id) => !form.fornecedores_desativados.includes(Number(id));

    const marcarFornecedor = (id, ativo) => {
        const fora = new Set(form.fornecedores_desativados);
        if (ativo) fora.delete(Number(id)); else fora.add(Number(id));
        alterar('fornecedores_desativados', [...fora].sort((a, b) => a - b));
    };

    const marcarDia = (dia, marcado) => {
        const atuais = new Set(diasMarcados);
        if (marcado) atuais.add(dia); else atuais.delete(dia);
        alterar('dias_semana', [...atuais].sort((a, b) => a - b).join(','));
    };

    // Erros por campo, com a mesma faixa que o backend recusaria. Validar aqui é
    // cortesia; a regra que vale continua sendo a de lá.
    const erros = useMemo(() => {
        if (!form) return {};
        const e = {};

        if (!HORA.test(form.horario)) e.horario = 'Use o formato HH:MM, de 00:00 a 23:59.';
        if (diasMarcados.length === 0) e.dias_semana = 'Marque pelo menos um dia.';

        for (const campo of CAMPOS) {
            const faixa = limites[campo];
            if (!faixa) continue;
            const n = Number(form[campo]);
            if (!form[campo] || Number.isNaN(n) || n < faixa.min || n > faixa.max) {
                e[campo] = `Informe um número de ${faixa.min} a ${faixa.max}.`;
            }
        }

        return e;
    }, [form, diasMarcados, limites]);

    const mudou = useMemo(
        () => !!form && !!inicial && JSON.stringify(form) !== JSON.stringify(inicial),
        [form, inicial],
    );

    const temErro = Object.keys(erros).length > 0;

    // Quem de fato recebe: ligado na lista, com numero no cadastro e fora da
    // regra de fabrica. E o numero que a confirmacao do envio precisa dizer.
    const totalNaProxima = useMemo(() => fornecedores
        .filter((f) => !f.fabrica && f.whatsapp && !form?.fornecedores_desativados?.includes(Number(f.fornecedor_id)))
        .reduce((soma, f) => soma + (f.na_proxima || 0), 0), [fornecedores, form]);

    const ativosComNumero = useMemo(() => fornecedores.filter(
        (f) => !f.fabrica && f.whatsapp && !form?.fornecedores_desativados?.includes(Number(f.fornecedor_id)),
    ).length, [fornecedores, form]);

    const salvar = async () => {
        if (temErro) {
            toast({ title: 'Revise os campos destacados.', status: 'warning', isClosable: true });
            return;
        }

        // Ligar ou desligar o disparo é o que muda a vida do fornecedor: só essa
        // mudança pede confirmação, para o resto não virar clique cansativo.
        if (form.envio_ativo !== inicial.envio_ativo) {
            const ligando = form.envio_ativo;
            const ok = await confirm({
                title: ligando ? 'Ligar o envio automático' : 'Desligar o envio automático',
                body: ligando
                    ? `O sistema volta a mandar WhatsApp sozinho para ${ativosComNumero} fornecedor(es), `
                      + `${form.itens_por_consulta} peça(s) por lista, nos dias ${diasMarcados.map(d => DIAS[d - 1].curto).join(', ')} às ${form.horario}. Confirma?`
                    : 'Nenhuma mensagem sai mais para fornecedor. As peças bloqueadas ficam esperando na fila até alguém religar. Confirma?',
                confirmText: ligando ? 'Ligar envio' : 'Desligar envio',
                cancelText: 'Cancelar',
                confirmColor: ligando ? 'green' : 'red',
            });
            if (!ok) return;
        }

        setSalvando(true);
        try {
            const data = await salvarConfigConsultaEstoque(paraEnvio(form));
            const atualizado = paraFormulario(data?.config || {});
            setForm(atualizado);
            setInicial(atualizado);
            if (data?.fornecedores) setFornecedores(data.fornecedores);
            toast({
                title: 'Configuração salva.',
                description: data?.mudancas?.length
                    ? `${data.mudancas.length} parâmetro(s) alterado(s).`
                    : 'Nenhum parâmetro mudou.',
                status: 'success',
                isClosable: true,
            });
            carregar();
        } catch (error) {
            const resposta = error?.response;
            const primeiro = resposta?.data?.errors
                ? Object.values(resposta.data.errors)[0]?.[0]
                : null;
            toast({
                title: resposta?.status === 403
                    ? 'Só administrador pode salvar esta configuração.'
                    : 'Erro ao salvar a configuração.',
                description: primeiro || resposta?.data?.message || null,
                status: 'error',
                duration: 8000,
                isClosable: true,
            });
        } finally {
            setSalvando(false);
        }
    };

    const restaurarPadrao = () => {
        // A trava do envio fica como esta: quem clica aqui quer os numeros de
        // volta ao padrao, nao desligar (ou ligar) o disparo sem perceber.
        setForm({
            ...paraFormulario(padrao),
            envio_ativo: form.envio_ativo,
            fornecedores_desativados: form.fornecedores_desativados,
        });
        toast({
            title: 'Valores do sistema carregados no formulário.',
            description: 'Nada foi salvo ainda: confira e clique em Salvar.',
            status: 'info',
            isClosable: true,
        });
    };

    if (carregando || !form) {
        return <Flex justify="center" py={10}><Spinner size="lg" color="orange.400" /></Flex>;
    }

    const resumoDias = diasMarcados.map((d) => DIAS[d - 1]?.curto).filter(Boolean).join(', ') || '-';

    return (
        <Box>
            {ConfirmPortal}

            {ensaio && (
                <Alert status="warning" borderRadius="md" mb={4} fontSize="sm" alignItems="flex-start">
                    <AlertIcon />
                    <Box>
                        <AlertTitle fontSize="sm">Modo ensaio ligado</AlertTitle>
                        <AlertDescription>
                            O fornecedor recebe e responde normalmente, mas a resposta não altera estoque,
                            bloqueio nem anúncio. Essa trava fica fora desta tela de propósito: só muda no .env.
                        </AlertDescription>
                    </Box>
                </Alert>
            )}

            {!podeSalvar && (
                <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
                    <AlertIcon />
                    Você vê os parâmetros em uso, mas alterar é só para administrador.
                </Alert>
            )}

            <Box borderWidth="1px" borderRadius="lg" p={{ base: 3, md: 5 }} mb={4} bg="white">
                <Flex justify="space-between" align="center" gap={4} wrap="wrap">
                    <Box>
                        <HStack spacing={3} mb={1}>
                            <Heading as="h4" size="sm">Envio automático</Heading>
                            <Badge colorScheme={form.envio_ativo ? 'green' : 'gray'} borderRadius="md" px={2}>
                                {form.envio_ativo ? 'Ligado' : 'Desligado'}
                            </Badge>
                        </HStack>
                        <Text fontSize="xs" color="gray.600" maxW="640px">
                            Ligado, o sistema pergunta sozinho nos dias e no horário abaixo. Desligado, a rotina
                            roda em simulação: registra o que faria e não manda mensagem nenhuma. O link criado à
                            mão na aba de consultas continua funcionando dos dois jeitos.
                        </Text>
                    </Box>
                    <Switch
                        size="lg"
                        colorScheme="green"
                        isChecked={form.envio_ativo}
                        isDisabled={!podeSalvar}
                        onChange={(e) => alterar('envio_ativo', e.target.checked)}
                    />
                </Flex>
            </Box>

            <VStack spacing={4} align="stretch">
                <Secao
                    titulo="Quando perguntar"
                    descricao={`Hoje: ${resumoDias} às ${form.horario}. Fim de semana e feriado nacional nunca entram, mesmo marcados.`}
                >
                    <FormControl isInvalid={!!erros.dias_semana} mb={4}>
                        <FormLabel fontSize="sm" fontWeight="semibold" mb={2}>Dias da semana</FormLabel>
                        <HStack spacing={4} wrap="wrap">
                            {DIAS.map((dia) => (
                                <Checkbox
                                    key={dia.valor}
                                    size="sm"
                                    colorScheme="orange"
                                    isChecked={diasMarcados.includes(dia.valor)}
                                    isDisabled={!podeSalvar}
                                    onChange={(e) => marcarDia(dia.valor, e.target.checked)}
                                >
                                    {dia.curto}
                                </Checkbox>
                            ))}
                        </HStack>
                        <FormHelperText fontSize="xs" color={erros.dias_semana ? 'red.500' : 'gray.600'}>
                            {erros.dias_semana
                                || 'Perguntar todo dia útil cobra o mesmo fornecedor três vezes na semana em que ele ainda está juntando a resposta anterior.'}
                        </FormHelperText>
                    </FormControl>

                    <FormControl isInvalid={!!erros.horario}>
                        <FormLabel fontSize="sm" fontWeight="semibold" mb={1}>Horário</FormLabel>
                        <Input
                            size="sm"
                            bg="white"
                            maxW="110px"
                            textAlign="center"
                            placeholder="14:00"
                            value={form.horario}
                            isDisabled={!podeSalvar}
                            onChange={(e) => alterar('horario', e.target.value.replace(/[^\d:]/g, '').slice(0, 5))}
                        />
                        <FormHelperText fontSize="xs" color={erros.horario ? 'red.500' : 'gray.600'}>
                            {erros.horario || 'As 14:00 são o maior vão do dia entre as listas de compras (08:30, 12:00 e 16:30).'}
                        </FormHelperText>
                    </FormControl>
                </Secao>

                <Secao
                    titulo="Tamanho da lista"
                    descricao="Quanto cabe em cada mensagem e por quanto tempo o fornecedor pode responder."
                >
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                        <CampoNumero
                            rotulo="Peças por lista"
                            unidade="peças"
                            ajuda="O fornecedor responde no celular, peça por peça: lista longa ninguém termina."
                            valor={form.itens_por_consulta}
                            limites={limites.itens_por_consulta}
                            somenteLeitura={!podeSalvar}
                            erro={erros.itens_por_consulta}
                            onChange={(v) => alterar('itens_por_consulta', v)}
                        />
                        <CampoNumero
                            rotulo="Validade do link"
                            unidade="dias"
                            ajuda="Depois disso o link expira e as peças voltam para a fila."
                            valor={form.dias_validade}
                            limites={limites.dias_validade}
                            somenteLeitura={!podeSalvar}
                            erro={erros.dias_validade}
                            onChange={(v) => alterar('dias_validade', v)}
                        />
                    </SimpleGrid>
                </Secao>

                <Secao
                    titulo="Quem entra na lista"
                    descricao="Só peça bloqueada por furo de fornecedor, do fornecedor titular e com WhatsApp cadastrado. Isso não muda aqui."
                >
                    <CampoNumero
                        rotulo="Bloqueio mínimo"
                        unidade="dias de bloqueio"
                        ajuda={`Não aceita menos de ${limites.dias_bloqueio_minimo?.min ?? 15}: esse é o prazo do bloqueio temporário, e perguntar antes é perguntar no meio do prazo, sobre uma falta que o fornecedor acabou de ter.`}
                        valor={form.dias_bloqueio_minimo}
                        limites={limites.dias_bloqueio_minimo}
                        somenteLeitura={!podeSalvar}
                        erro={erros.dias_bloqueio_minimo}
                        onChange={(v) => alterar('dias_bloqueio_minimo', v)}
                    />
                </Secao>

                <Secao
                    titulo="Fornecedores que participam"
                    descricao={`A rotina pergunta a quem está ligado aqui e tem peça esperando. ${ativosComNumero} fornecedor(es) participam; ${totalNaProxima} peça(s) entrariam na próxima rodada. O número é o do cadastro e só muda lá.`}
                >
                    {fornecedores.length === 0 ? (
                        <Text fontSize="sm" color="gray.500">
                            Nenhum fornecedor com peça bloqueada por furo no momento.
                        </Text>
                    ) : (
                        <TableContainer>
                            <Table size="sm" variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>Fornecedor</Th>
                                        <Th>WhatsApp</Th>
                                        <Th isNumeric>
                                            <Tooltip
                                                label="Quantas peças entrariam na lista dele se a rodada fosse agora: já passaram do bloqueio mínimo, não estão na carência e ele não tem lista aberta."
                                                hasArrow
                                            >
                                                <Text as="span" borderBottom="1px dotted" borderColor="gray.400" cursor="help">
                                                    Na próxima
                                                </Text>
                                            </Tooltip>
                                        </Th>
                                        <Th isNumeric>
                                            <Tooltip label="Tudo que está bloqueado por furo nesse fornecedor, inclusive o que ainda está no prazo." hasArrow>
                                                <Text as="span" borderBottom="1px dotted" borderColor="gray.400" cursor="help">
                                                    Bloqueadas
                                                </Text>
                                            </Tooltip>
                                        </Th>
                                        <Th textAlign="center">Pergunta?</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {fornecedores.map((f) => {
                                        const ligado = participa(f.fornecedor_id) && !f.fabrica;
                                        return (
                                            <Tr key={f.fornecedor_id} opacity={ligado ? 1 : 0.6}>
                                                <Td>
                                                    <Text fontSize="sm" fontWeight="medium">{f.fornecedor}</Text>
                                                    {f.fabrica && (
                                                        <Badge colorScheme="purple" fontSize="0.65rem" borderRadius="md">
                                                            fábrica
                                                        </Badge>
                                                    )}
                                                </Td>
                                                <Td>
                                                    {f.whatsapp ? (
                                                        <Text fontSize="sm" color="gray.700">{telefone(f.whatsapp)}</Text>
                                                    ) : (
                                                        <Tooltip label="Sem número no cadastro do fornecedor: a rotina pula e avisa no log." hasArrow>
                                                            <Badge colorScheme="red" borderRadius="md">sem número</Badge>
                                                        </Tooltip>
                                                    )}
                                                </Td>
                                                <Td isNumeric fontSize="sm" fontWeight={f.na_proxima > 0 ? 'bold' : 'normal'}>
                                                    {f.na_proxima}
                                                </Td>
                                                <Td isNumeric fontSize="sm" color="gray.600">{f.bloqueadas}</Td>
                                                <Td textAlign="center">
                                                    {f.fabrica ? (
                                                        <Tooltip label="Fornecedor de fábrica: o WhatsApp dele só recebe o pedido semanal." hasArrow>
                                                            <Text fontSize="xs" color="gray.500">não se aplica</Text>
                                                        </Tooltip>
                                                    ) : (
                                                        <Switch
                                                            size="sm"
                                                            colorScheme="green"
                                                            isChecked={ligado}
                                                            isDisabled={!podeSalvar}
                                                            onChange={(e) => marcarFornecedor(f.fornecedor_id, e.target.checked)}
                                                        />
                                                    )}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    )}
                </Secao>

                <Secao
                    titulo="Quando perguntar de novo"
                    descricao="Peça já perguntada sai da fila por um tempo. Sem isso o fornecedor recebe a mesma pergunta assim que o link vence."
                >
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                        <CampoNumero
                            rotulo='Após "não tenho mais"'
                            unidade="dias"
                            ajuda="Conta da data da resposta. Peça que o fornecedor diz não ter costuma demorar a voltar."
                            valor={form.dias_apos_nao_tem}
                            limites={limites.dias_apos_nao_tem}
                            somenteLeitura={!podeSalvar}
                            erro={erros.dias_apos_nao_tem}
                            onChange={(v) => alterar('dias_apos_nao_tem', v)}
                        />
                        <CampoNumero
                            rotulo="Sem resposta"
                            unidade="dias"
                            ajuda="Conta do envio da lista. Vale também para a peça que ficou em branco numa lista respondida pela metade."
                            valor={form.dias_apos_sem_resposta}
                            limites={limites.dias_apos_sem_resposta}
                            somenteLeitura={!podeSalvar}
                            erro={erros.dias_apos_sem_resposta}
                            onChange={(v) => alterar('dias_apos_sem_resposta', v)}
                        />
                    </SimpleGrid>
                </Secao>
            </VStack>

            <Divider my={5} />

            <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                <Text fontSize="xs" color="gray.600">
                    {autoria.em
                        ? `Última alteração em ${dayjs(autoria.em).format('DD/MM/YYYY HH:mm')}${autoria.por ? ` por ${autoria.por}` : ''}.`
                        : 'Nunca alterado por aqui: os valores são os do sistema.'}
                </Text>

                <HStack spacing={3}>
                    <Tooltip label="Preenche o formulário com os valores do sistema. Não salva." hasArrow>
                        <Button
                            size="sm"
                            variant="outline"
                            leftIcon={<FaUndo />}
                            onClick={restaurarPadrao}
                            isDisabled={!podeSalvar || salvando}
                        >
                            Valores do sistema
                        </Button>
                    </Tooltip>
                    <Button
                        size="sm"
                        colorScheme="green"
                        leftIcon={<FaSave />}
                        onClick={salvar}
                        isLoading={salvando}
                        isDisabled={!podeSalvar || !mudou || temErro || salvando}
                    >
                        Salvar configuração
                    </Button>
                </HStack>
            </Flex>

            {mudou && !temErro && (
                <Text fontSize="xs" color="orange.600" mt={2} textAlign="right">
                    Alterações pendentes:{' '}
                    {CAMPOS.filter((c) => JSON.stringify(form[c]) !== JSON.stringify(inicial[c]))
                        .map((c) => ROTULOS[c])
                        .join(', ')}
                    .
                </Text>
            )}
        </Box>
    );
}
